import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { t } from '@/utils/i18n';
import { isNumber } from '@/utils/isNumber';
import { getParams, isEmbroideryMode } from '@/utils/params';
import { usePriceFormatter } from '@/hooks/usePrice';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { PriceInfo } from '@/components/common/PriceInfo';
import { CaretRight } from '@/components/common/icons/Caret';
import './SizeSelectionPrice.scss';

interface DiscountInfoProps {
  className: string;
  discountBreakdown: DiscountBreakdown;
  itemCount: number;
}

export const DiscountInfo: FunctionComponent<DiscountInfoProps> = ({
  className,
  discountBreakdown,
  itemCount,
}) => {
  const currentDiscount = discountBreakdown.currentDiscount;
  if (isNumber(currentDiscount)) {
    return (
      <div className={className}>
        {t('basket.discountNow', currentDiscount.toFixed(0), itemCount.toFixed(0))}
      </div>
    );
  }

  const { nextDiscountFrom, nextDiscount } = discountBreakdown;
  if (isNumber(nextDiscountFrom) && isNumber(nextDiscount)) {
    return (
      <div className={className}>
        {t('basket.discountItem', nextDiscountFrom.toFixed(0), nextDiscount.toFixed(0))}
      </div>
    );
  }
  return null;
};

interface Props {
  selectedSizesItemCount: number;
  totalItemCount: number;
  discountBreakdown: DiscountBreakdown;
  onDetails: () => void;
}

export const SizeSelectionPrice: FunctionComponent<Props> = ({
  selectedSizesItemCount,
  totalItemCount,
  discountBreakdown,
  onDetails,
}) => {
  const priceFormatter = usePriceFormatter();
  const configurations = useSelector(selectConfigurations);

  const printPriceTranslationKey =
    configurations.length > 0
      ? isEmbroideryMode()
        ? 'embroidery.labels.priceFaq'
        : 'basket.priceFaq'
      : '';

  const productPrice = priceFormatter(discountBreakdown.product);
  const reducedProduct = discountBreakdown.reducedProduct;
  const reducedProductPrice = isNumber(reducedProduct) ? priceFormatter(reducedProduct) : undefined;

  const total = discountBreakdown.total;
  const totalPrice = priceFormatter(total);
  const reducedTotal = discountBreakdown.reducedTotal;
  const reducedTotalPrice = isNumber(reducedTotal) ? priceFormatter(reducedTotal) : undefined;

  return (
    <div className="size-selection-price">
      <Link to="/price/details" className="size-selection-price-details" onClick={onDetails}>
        <div className="size-selection-item-price">
          {t('basket.itemPrice')}
          :&nbsp;
          {reducedProductPrice ? (
            <span aria-label={t('sizePanel.discount', productPrice, reducedProductPrice)}>
              {reducedProductPrice}
              <del className="size-selection-item-price__original">{productPrice}</del>
            </span>
          ) : (
            productPrice
          )}
          <DiscountInfo
            className="size-selection-item-price__discount"
            discountBreakdown={discountBreakdown}
            itemCount={totalItemCount}
          />
        </div>
        <div className="size-selection-price-details__link">
          <span>{t('basket.details')}</span>
          <CaretRight />
        </div>
      </Link>
      <div className="size-selection-total-price">
        <div className="size-selection-total-price__item-count">
          {t('basket.selectedItem', selectedSizesItemCount.toFixed(0))}
        </div>
        <div className="size-selection-total-price__amount">
          {t('basket.totalCosts')}
          :&nbsp;
          {reducedTotalPrice ? (
            <span aria-label={t('sizePanel.discount', totalPrice, reducedTotalPrice)}>
              {reducedTotalPrice}
              <del className="size-selection-total-price__original">{totalPrice}</del>
            </span>
          ) : (
            totalPrice
          )}
        </div>
      </div>
      <PriceInfo
        className="size-selection-price-info"
        printPriceTranslationKey={printPriceTranslationKey}
        showVatIncludedString={getParams().locale !== 'us_AU' || (reducedTotal || total) < 1000}
      />
    </div>
  );
};
